/// FOURJS_START_COPYRIGHT(D,2018)
/// Property of Four Js*
/// (c) Copyright Four Js 2018, 2026. All Rights Reserved.
/// * Trademark of Four Js Development Tools Europe Ltd
///   in the United States and elsewhere
///
/// This file can be modified by licensees according to the
/// product manual.
/// FOURJS_END_COPYRIGHT

"use strict";

modulum("LogPlayerService", ["LogService"], function (context, cls) {
  /**
   * Log Player Service to centralize log playing logic used in LogPlayerWidget and in UR Log Player
   * @namespace gbc.LogPlayerService
   * @gbcService
   */
  class LogPlayerService /** @lends gbc.LogPlayerService */ {
    constructor() {
      /**
       * @type {Map<number, Map<number, classes.VMApplication>>}
       */
      this._fakeSessions = null;
      this._appStack = null;
      this._isLogReplay = false;
      /** @type {cls.LogPlayerWidget} */
      this._logPlayerWidget = null;
    }

    /**
     * Return true if a log is being replayed
     * @return {boolean}
     */
    isLogReplay() {
      return this._isLogReplay;
    }

    /**
     * Set status of log replay
     * @param value
     */
    setLogReplay(value) {
      this._isLogReplay = value;
    }

    /**
     * Get the mock application registered with a session id and an application id
     * @param {number} sessionId
     * @param {number} appId
     * @param {number} procId
     * @param {boolean} dontCreate
     * @returns {classes.VMApplication}
     */
    getApplication(sessionId, appId, procId, dontCreate) {
      if (!this._fakeSessions) {
        this._fakeSessions = new Map();
      }
      if (!this._appStack) {
        this._appStack = [];
      }
      if (!this._fakeSessions.has(sessionId)) {
        this._fakeSessions.set(sessionId, new Map());
      }
      if (appId === null) {
        appId = procId;
      }
      const fakeSession = this._fakeSessions.get(sessionId);
      if (!fakeSession.has(appId) && !dontCreate) {
        const app = globalThis.gbc.MockService.fakeApplication(false, appId, procId);
        app.setRunning(true);
        app.info().procId = procId;
        app.procId = procId;
        // replace last path segment with webcomponent ressource
        if (gbc.buildTool === "vite") {
          app.info().webComponent = globalThis.location.origin + "/ua/w/web/src/resources/webcomponents";
        } else {
          app.info().webComponent =
            globalThis.location.origin + globalThis.location.pathname.replace(/\/[^/]+$/, "/") + context.ThemeService.getResource("webcomponents");
        }
        fakeSession.set(appId, app);

        this._appStack.push(app);
        app.dvm.when(context.constants.baseEvents.gotRN0, () => {
          globalThis.requestAnimationFrame(() => {
            let session = app.getSession();
            app.stop();
            let appIndex = this._appStack.indexOf(app);
            if (appIndex >= 0) {
              let nextApp = this._appStack[appIndex - 1];
              this._appStack.splice(appIndex, 1);
              if (!nextApp) {
                nextApp = this._appStack.at(-1);
              }
              if (nextApp) {
                nextApp.getSession().getWidget().setCurrentWidget(nextApp.getUI().getWidget());
              }
            }
            if (session && session.getApplications() && !session.getApplications().length) {
              session.destroy();
            }
          });
        });
      }
      return fakeSession.get(appId);
    }

    /**
     * Set a given app visible
     * @param {classes.VMApplication} app- application to set visible
     */
    setVisibleApplication(app) {
      const session = app.getSession();
      if (session) {
        session.getWidget().setCurrentWidget(app.getUI().getWidget());
      }
    }

    /**
     * Unregister a mock application
     * @param sessionId
     * @param appId
     */
    removeApplication(sessionId, appId) {
      if (!this._fakeSessions) {
        this._fakeSessions = new Map();
      }
      const session = this._fakeSessions.get(sessionId);
      if (session) {
        session.delete(appId);
        if (!session.size) {
          this._fakeSessions.delete(sessionId);
        }
      }
    }

    /**
     * Clean all applications/session
     */
    cleanApplications() {
      if (this._fakeSessions) {
        this._fakeSessions.clear();
      }
    }

    /**
     * Replace resource reference with mock ones
     * @param {string} order
     * @param {Object?} imageMap
     * @returns {string}
     */
    mockOrderResources(order, imageMap) {
      const imgMock = context.ThemeService.getResource("img/logo.png"),
        ttfMock = context.ThemeService.getResource("fonts/materialdesignicons-webfont.ttf");

      const imgReplacer = function (tpl, data) {
        return tpl.toString().replaceAll(/([^"]+\.(?:png|jpg|gif|svg))(?:\?[^\\"]+)?/g, function (id) {
          return (data && data[id]) || imgMock;
        });
      };

      order = imgReplacer(order, imageMap);

      return order
        .replaceAll(/"[^"]+\.ttf\?[^"]+"/g, '"' + ttfMock + '"') // Fonts
        .replaceAll(/componentType "[^"]+"/g, 'componentType "empty"'); // webcomponents
    }

    /**
     * Play a defined amount of steps, from the current step
     * @param stepCount Amount of steps to play. Play the whole log if sets to 0. (Default is 0)
     */
    play(stepCount) {
      this.getWidget()?.play(stepCount);
    }

    /**
     * Pause the log execution
     */
    pause() {
      this.getWidget()?.pause();
    }

    /**
     * Play until the step is reached
     * @param {number} stepToReach Step to reach. The step will be played
     */
    forward(stepToReach) {
      this.getWidget()?.forward(stepToReach);
    }

    /**
     * Add/Remove an overlay upon the app,
     * preventing the user from interacting with the application
     * @param {boolean} allow True to allow user interaction.
     * False to prevent user from clicking on the page
     */
    allowUserInteraction(allow) {
      this.getWidget()?.addOverlay(!allow, true);
    }

    onReady(hook) {
      return this.getWidget()?.when(context.constants.widgetEvents.ready, hook);
    }

    onError(hook) {
      return this.getWidget()?.when(context.constants.widgetEvents.logPlayerError, hook);
    }

    //#region LogPlayerWidget

    /**
     * Create the LogPlayerWidget,
     * add the MainContainerWidget as a child
     * and return the LogPlayerWidget
     * @param {classes.MainContainerWidget} mainWidget
     * @returns {classes.LogPlayerWidget}
     */
    createWidget(mainWidget) {
      this._logPlayerWidget = cls.WidgetFactory.createWidget("LogPlayer", {
        appHash: gbc.systemAppId
      });
      this._logPlayerWidget.addChildWidget(mainWidget);
      return this._logPlayerWidget;
    }

    /**
     * Return the LogPlayerWidget if it was created
     * `null` otherwise
     * @returns {classes.LogPlayerWidget|null}
     */
    getWidget() {
      if (!this._logPlayerWidget) {
        gbc.LogService.warn("LogPlayerWidget was not created");
      }
      return this._logPlayerWidget;
    }
    //#endregion
  }

  gbc.LogPlayerService = new LogPlayerService();
});
